import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { PaymentType } from "@shared/types/common";
import { GetAQuoteAddons } from "@shared/types/localTypes/marketo.ts";

export const useUpgradeStore = defineStore("upgrade", () => {
  // State
  const isQuickUpgradeState = ref<boolean>(false);
  const upgradeViewIsVisibleState = ref<boolean>(false);
  const buyModalIsOpenState = ref<boolean>(false);
  const upgradeModalIsOpenState = ref<boolean>(false);
  const getAQuoteModalIsOpenState = ref<boolean>(false);
  const addBundleModalIsOpenState = ref<boolean>(false);
  const pendingActionState = ref<(() => void) | null>(null);
  const buyFlowIsProcessingState = ref<boolean>(false);
  const upgradeFlowIsProcessingState = ref<boolean>(false);
  const selectedPaymentTypeState = ref<PaymentType | undefined>(undefined);
  const currentUpgradeSelectedState = ref<{
    [key: string]: GetAQuoteAddons;
  } | null>(null);

  // Getters
  const isQuickUpgrade = computed(() => isQuickUpgradeState.value);
  const upgradeViewIsVisible = computed(() => upgradeViewIsVisibleState.value);
  const buyModalIsOpen = computed(() => buyModalIsOpenState.value);
  const upgradeModalIsOpen = computed(() => upgradeModalIsOpenState.value);
  const getAQuoteModalIsOpen = computed(() => getAQuoteModalIsOpenState.value);
  const addBundleModalIsOpen = computed(() => addBundleModalIsOpenState.value);
  const pendingAction = computed(() => pendingActionState.value);
  const buyFlowIsProcessing = computed(() => buyFlowIsProcessingState.value);
  const upgradeFlowIsProcessing = computed(
    () => upgradeFlowIsProcessingState.value,
  );
  const selectedPaymentType = computed(() => selectedPaymentTypeState.value);
  const upgradeSelected = computed(() => currentUpgradeSelectedState.value);

  // Actions
  const setUpgradeSelected = (
    paymentType: PaymentType,
    getAQuoteAddons: GetAQuoteAddons,
  ) => {
    currentUpgradeSelectedState.value = {
      ...currentUpgradeSelectedState.value,
      [paymentType]: getAQuoteAddons,
    };
  };

  const setSelectedPaymentType = (paymentType: PaymentType) => {
    selectedPaymentTypeState.value = paymentType;
  };

  const dismissUpgradeView = () => {
    if (buyFlowIsProcessingState.value || upgradeFlowIsProcessingState.value) {
      return;
    }
    upgradeViewIsVisibleState.value = false;
    buyModalIsOpenState.value = false;
    upgradeModalIsOpenState.value = false;
    getAQuoteModalIsOpenState.value = false;
    addBundleModalIsOpenState.value = false;
    pendingActionState.value = null;
    document.documentElement.classList.remove("upgrade-modal-opened");
  };

  const displayUpgradeView = () => {
    isQuickUpgradeState.value = false;
    upgradeViewIsVisibleState.value = true;
    document.documentElement.classList.add("upgrade-modal-opened");
  };

  const setBuyModalIsOpen = (isOpen: boolean) => {
    buyModalIsOpenState.value = isOpen;
  };

  const setUpgradeModalIsOpen = (isOpen: boolean) => {
    upgradeModalIsOpenState.value = isOpen;
  };

  const setGetAQuoteModalIsOpen = (isOpen: boolean) => {
    getAQuoteModalIsOpenState.value = isOpen;
  };

  const setAddBundleModalIsOpen = (isOpen: boolean) => {
    addBundleModalIsOpenState.value = isOpen;
  };

  const setPendingAction = (action: (() => void) | null) => {
    pendingActionState.value = action;
  };

  const executePendingAction = () => {
    if (pendingActionState.value) {
      pendingActionState.value();
      pendingActionState.value = null;
    }
  };

  const setBuyFlowIsProcessing = (isProcessing: boolean) => {
    buyFlowIsProcessingState.value = isProcessing;
  };

  const setUpgradeFlowIsProcessing = (isProcessing: boolean) => {
    upgradeFlowIsProcessingState.value = isProcessing;
  };

  const setIsQuickUpgrade = (isQuick: boolean) => {
    isQuickUpgradeState.value = isQuick;
  };

  return {
    isQuickUpgrade,
    upgradeViewIsVisible,
    buyModalIsOpen,
    upgradeModalIsOpen,
    getAQuoteModalIsOpen,
    addBundleModalIsOpen,
    pendingAction,
    buyFlowIsProcessing,
    upgradeFlowIsProcessing,
    selectedPaymentType,
    upgradeSelected,
    setUpgradeSelected,
    setSelectedPaymentType,
    dismissUpgradeView,
    displayUpgradeView,
    setBuyModalIsOpen,
    setUpgradeModalIsOpen,
    setGetAQuoteModalIsOpen,
    setAddBundleModalIsOpen,
    setPendingAction,
    executePendingAction,
    setBuyFlowIsProcessing,
    setUpgradeFlowIsProcessing,
    setIsQuickUpgrade,
  };
});
